import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@thinknet-gmbh/platform-common-ui';
import { ApplicantStatusesEnum, EmployeeCreationTypeEnum } from '@thinknet-gmbh/platform-common';

import { useGetCurrentCompanyId } from 'src/hooks/companies/get-current-company-id.hook';
import { useGetVacancyById } from 'src/hooks/vacancies-patterns/get-vacancy-by-id.hook';
import { useCreateEmployeeQuestionnaire } from 'src/hooks/employee-questionnaire/create-employee-questionnaire.hook';
import { useUpdateApplicantById } from 'src/hooks/applicants/update-applicant-by-id.hook';

import { EmployeeByQuestionnaireModalForm } from './components/employee-by-questionnaire-modal-form';
import { ICreateEmployeeFormInitialValue } from 'src/pages/employee/sub-pages/create-employee-by-questionnaire/create-employee-by-questionnaire.interfaces';

interface IProps {
  isOpened: boolean;
  onClose: () => void;
  applicant: {
    _id: string;
    firstName?: string;
    lastName?: string;
    email: string;
    vacancyId: string;
  };
}

const CreateEmployeeByQuestionnaireModal: FC<IProps> = ({ isOpened, onClose, applicant }) => {
  const { t } = useTranslation(['common', 'employee']);

  const { vacancy = {} } = useGetVacancyById(applicant.vacancyId);

  const currentCompanyId = useGetCurrentCompanyId();

  const { company, job, jobLocation } = vacancy || {};

  const { createEmployeeQuestionnaire, loading } = useCreateEmployeeQuestionnaire({
    onSuccess: () => onClose(),
  });

  const initialValues = {
    companyId: currentCompanyId || '',
    jobCompanyId: company?._id || '',
    creationType: EmployeeCreationTypeEnum.questionnaire,
    questionnaireSettingsId: '',
    departmentId: jobLocation?.companyDepartmentId || '',
    jobId: job?._id || '',
    jobLocationId: jobLocation?._id || '',
    responsiblePersonsIds: [],
    firstName: applicant?.firstName || '',
    lastName: applicant?.lastName || '',
    email: applicant?.email || '',
  };

  const applicantId = applicant?._id;

  const { updateApplicant } = useUpdateApplicantById({});

  const onFinish = (values: ICreateEmployeeFormInitialValue) => {
    const { firstName, email, lastName, ...rest } = values;

    createEmployeeQuestionnaire({
      variables: {
        payload: {
          ...rest,
          employeePersonalInfo: {
            firstName,
            lastName,
            email,
          },
          companyId: currentCompanyId,
          applicantId,
        },
      },
      onCompleted: data => {
        if (applicantId && data) {
          updateApplicant({
            variables: {
              applicantId,
              payload: { status: ApplicantStatusesEnum.QUESTIONNAIRE_SENT },
            },
          });
        }
      },
    });
  };

  return (
    <Modal
      isOpened={isOpened}
      onClose={onClose}
      title={t('vacancies:buttons.takeApplicantAsEmployee')}
    >
      <EmployeeByQuestionnaireModalForm
        initialQuestionnaireSettingsIds={job?.questionnaireSettingsIds || []}
        loading={loading}
        initialValues={initialValues}
        onFinish={onFinish}
        onClose={onClose}
      />
    </Modal>
  );
};

export default CreateEmployeeByQuestionnaireModal;
